import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { formatISO } from 'date-fns';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { defaultColors } from 'src/app/classes/widget/chartHandlerV2';
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { ChartService } from 'src/app/services/api/charts/chart.service';
import { EventService } from 'src/app/services/api/events/event.service';
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { TrackService } from 'src/app/services/api/tracks/track.service';
import { ButtonComponent, ColorPickerComponent, DatePickerComponent } from 'ui-library';
import { IconPickerComponent } from 'projects/ui-library/src/lib/components/forms/icon-picker/icon-picker.component';
import { FieldLabelComponent } from 'src/app/components/project-pages/dashboard/field-label/field-label.component';

interface WidgetInput {
  type: 'select' | 'text' | 'number' | 'checkbox' | 'radio' | 'date' | 'colors' | 'color' | 'datetime-local',
  name: string;
  default?: any,
  multiple?: boolean,
  value?: any,
  options?: any[],
  update?: any,
  groupKey?: boolean,
  group?: string,
  subgroup?: string,
  subgroupKey?: boolean,
  subgroupOptions?: any[],
  placeholder?: string,
  // A color input: the theme color (an empty value) offered.
  auto?: boolean
}

// The distinct values of a field over every project of a tenant (one aggregation, all projects merged).
export function tenantValuesQuery(collection: string, field: string): any {
  return {
    collection,
    field: { [field]: { type: 'string', options: { sort: 'category', order: 'ascending', limit: 500 } } },
    aggregation: { _id: { type: 'string', options: { aggregate: 'count' } } },
    byProject: false,
    limit: 1000000
  };
}

@Component({
  selector: 'app-widget-form',
  imports: [
    ColorPickerComponent,
    FieldLabelComponent,
    IconPickerComponent,
    CommonModule,
    MatIconModule,
    MatMenuModule,
    TranslateModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatRadioModule,
    MatInputModule,
    FormsModule,
    ButtonComponent,
    DatePickerComponent
  ],
  standalone: true,
  templateUrl: './widget-form.component.html',
  styleUrl: './widget-form.component.scss'
})

export class WidgetFormComponent implements OnInit, OnDestroy {
  // Services
  private readonly metricService: MetricService = inject(MetricService);
  private readonly eventService: EventService = inject(EventService);
  private readonly trackService: TrackService = inject(TrackService);
  private readonly attachmentService: AttachmentService = inject(AttachmentService);
  private readonly chartService: ChartService = inject(ChartService);

  // Component API
  @Input() form: any;
  @Input() version: number = 0;
  @Input() field: string = '';
  @Input() category: string = '';
  // Synthesis: the values of a filter are those of every project of the tenant, not of the open project.
  @Input() tenantId: string | undefined;
  @Output('onUpdate') onUpdate = new EventEmitter<any>();
  @Output('onActions') onActions = new EventEmitter<any>();

  // Variables
  title: string = "My form";
  inputs: WidgetInput[] = [];
  groupInputs: WidgetInput[] = [];
  subgroupInputs: WidgetInput[] = [];
  options: any[] = [];
  valueSub: any;
  types: Record<string, string> = {
    "ObjectID": "title",//play_arrow
    "string": "title",
    "number": "format_list_numbered",
    "boolean": "tonality",//tonality, brightness_6
    "Mixed": "arrow_right",//play_arrow
    "date": "date_range",
    "Array": "arrow_right"
  };
  groups: Record<string, any[]> = {};
  groupKeys: Record<string, string> = {};
  subgroups: Record<string, any[]> = {};
  subgroupKeys: Record<string, string> = {};
  projectTimezone: number = 0;
  isInitialized: boolean = false;
  loadingList: boolean = false;
  destroy$ = new Subject<void>();
  icon: string = "arrow_right";
  private _validDateCache = new Map<any, string>();

  constructor() { }

  ngOnInit(): void {
    if (this.form && this.form.options) {
      this.inputs = this.form.options.map((o: any) => { return { ...o } })

      if ((this.form.type == 'objectid' || this.form.type == 'string') && (this.field == 'filters' || this.field == 'xFilter' || this.field == 'yFilter')) {
        this.getList();
      } else {
        this.setValues();
      }

      let subgroupKeys = this.inputs.filter(input => this.isSubgroupKey(input));

      subgroupKeys.forEach((input: any) => {
        if (input.subgroup) {
          this.subgroupKeys[input.subgroup] = input.value !== undefined ? input.value : input.default;
        }
      });

      this.subgroupInputs = this.inputs.filter(input => this.isSubgroupElement(input));

      this.subgroupInputs.forEach((input: any) => {
        if (input.subgroup) {
          if (!this.subgroups[input.subgroup]) {
            this.subgroups[input.subgroup] = [];
          }
          if (input.subgroup) {
            (this.subgroups[input.subgroup] ??= []).push(input);
          }
        }
      });

      this.groupInputs = this.inputs.filter(input => this.isGroupElement(input));

      this.groupInputs.forEach((input: any) => {
        if (!this.groups[input.group])
          this.groups[input.group] = [];

        if (this.isSubgroupElement(input)) {
          let subgroupKey = this.subgroupKeys[input.subgroup];

          if (subgroupKey != undefined && input.subgroupOptions && input.subgroupOptions.includes(subgroupKey)) {
            (this.groups[input.group] ??= []).push(input);

            if (input.subgroupWidth) {
              input.width = input.subgroupWidth[subgroupKey.toLowerCase()] || input.width;
            }
          }
        } else
          (this.groups[input.group] ??= []).push(input);
      });

      let groupKeys = this.inputs.filter(input => this.isGroupKey(input));

      groupKeys.forEach((input: any) => {
        this.groupKeys[input.group] = input.value != undefined ? input.value : input.default;
      });

      this.title = this.form.label || this.form.name;
      this.icon = this.form.icon || "arrow_right"
      this.inputs = this.inputs.filter(input => !this.isGroupElement(input));
    }
  }

  ngOnDestroy(): void {
    this._validDateCache.clear();

    if (this.valueSub)
      this.valueSub.unsubscribe();

    this.destroy$.next();
    this.destroy$.complete();
  }

  // Design sections switched on by a "display" checkbox: the switch in the header, the other settings below.
  get activation(): any {
    return this.field === 'design' ? this.inputs.find(input => input.type === 'checkbox' && input.name === 'display') : undefined;
  }

  bodyInputs(): any[] {
    const activation = this.activation;
    return activation ? this.inputs.filter(input => input !== activation) : this.inputs;
  }

  getList() {
    this.loadingList = true;

    const handleResult = (res: any[]) => {
      this.options = res.filter(r => r._id).map(r => r._id);
      this.options.sort((a, b) => a > b ? 1 : -1)

      this.inputs.forEach(input => {
        if (input.name == "values") {
          input.options = this.options;
          this.setValues();
        }
      })

      this.loadingList = false;

      if (this.valueSub)
        this.valueSub.unsubscribe();
    }

    if (this.tenantId && this.category) {
      this.valueSub = this.chartService.aggregateTenant(this.tenantId, tenantValuesQuery(this.category, this.form.name))
        .pipe(takeUntil(this.destroy$)).subscribe({
          next: result => handleResult((result.rows ?? []).map((row: any) => ({ _id: row.x }))),
          error: () => handleResult([])
        });
    } else if (this.category == "metric") {
      this.valueSub = this.metricService.getValueList(this.form.name).pipe(takeUntil(this.destroy$)).subscribe(handleResult)
    } else if (this.category == "track") {
      this.valueSub = this.trackService.getValueList(this.form.name).pipe(takeUntil(this.destroy$)).subscribe(handleResult)
    } else if (this.category == "event") {
      this.valueSub = this.eventService.getValueList(this.form.name).pipe(takeUntil(this.destroy$)).subscribe(handleResult)
    } else if (this.category == "attachment") {
      this.valueSub = this.attachmentService.getValueList(this.form.name).pipe(takeUntil(this.destroy$)).subscribe(handleResult)
    }
  }

  isGroupElement(input: WidgetInput) {
    if (input.group && !input.groupKey) return true;
    return false;
  }

  isSubgroupElement(input: WidgetInput) {
    if (input.subgroup && !input.subgroupKey) return true;
    return false;
  }

  isGroupKey(input: WidgetInput) {
    if (input.groupKey != undefined) return true;
    return false;
  }

  isSubgroupKey(input: WidgetInput) {
    if (input.subgroupKey != undefined) return true;
    return false;
  }


  getGroupInputs(groupName: string | number) {
    if (this.groups[groupName]) return this.groups[groupName];
    return [];
  }

  setValues() {
    this.inputs.forEach(input => {
      if (input.type == "text" || input.type == "number" || input.type == "radio" || input.type == "checkbox") {
        input.value = input.value !== undefined ? input.value : input.default

        if (input.placeholder)
          input.placeholder = input.placeholder;
      } else if (input.type == "datetime-local") {
        input.value = input.value !== undefined ? input.value : input.default;
      } else if (input.type == "color") {
        input.value = input.value !== undefined ? input.value : input.default;
      } else if (input.type == "colors") {
        if (!input.options) {
          input.options = defaultColors.map((c, i) => {
            return {
              value: c,
              label: i
            }
          })
        } else {
          input.options.sort((a, b) => {
            if (isNaN(a.label) && !isNaN(b.label)) {
              return -1;
            }

            if (!isNaN(a.label) && isNaN(b.label)) {
              return 1;
            }

            return 0;
          })
        }
      } else if (input.type == "select") {
        input.value = input.value || input.default;

        if (input.options) {
          input.options = input.options.map(o => {
            if (o && o.value) {
              return o
            } else {
              return {
                name: o,
                value: o
              }
            }
          })
        }
      }

      input.update = (event: { target: { value: any; }; }, index: number, type: string) => {
        // The colour list itself (not the first input of the section: its switch comes first).
        const color = input.type == "colors" && index >= 0 ? input.options?.[index] : undefined;
        if (color) {
          if (type == 'color') color.value = event.target.value;
          if (type == 'label') color.label = event.target.value;
        }

        if (input.type == "datetime-local") {
          input.value = formatISO(new Date(event.target.value))
        }

        if (input.type == "select" && input.subgroupKey) {
          this.switchSubgroupElements(input);
        }

        if (this.onUpdate) {
          this.onUpdate.emit({ field: this.field, form: this.form, inputs: this.inputs.concat(this.groupInputs).concat(this.subgroupInputs), index })
        }
      }
    })
  }

  switchSubgroupElements(input: WidgetInput) {
    // Vérification que input.group existe
    if (!input.group) return;

    let subgroupKey: string = input.subgroup ?? '';
    subgroupKey = input.value ?? '';
    let removedInputIndexes: number[] = [];
    let groupInputs = this.groups[input.group];

    // Vérification que groupInputs existe
    if (!groupInputs) return;

    try {
      if (!groupInputs.length) return;

      // Removing subgroup fields that are no longer selected
      for (let i = 0; i < groupInputs.length; i++) {
        if (this.isSubgroupElement(groupInputs[i]) && !groupInputs[i].subgroupOptions?.includes(subgroupKey)) {
          let removedInputIndex = groupInputs!.findIndex((grInput: { name: any; }) => grInput.name == groupInputs![i].name);

          groupInputs!.splice(removedInputIndex, 1, {}); // replacing group input with index placeholder
          removedInputIndexes.push(removedInputIndex);
        }
      }

      this.subgroupInputs.forEach(subgroupInput => {
        if (!subgroupInput.subgroupOptions || !subgroupInput.subgroupOptions.includes(subgroupKey)) return;

        if (groupInputs!.findIndex((grInput: { name: string; }) => grInput.name == subgroupInput.name) >= 0) return;

        let subgroupIndex = -1;

        if (!removedInputIndexes.length) {
          groupInputs!.forEach((groupInput: WidgetInput, index: number) => {
            if (this.isSubgroupElement(groupInput))
              subgroupIndex = index;
          });
        } else {
          subgroupIndex = removedInputIndexes[0] ?? -1;
        }

        if (subgroupIndex == -1) // if no removed subgroup inputs and no existing subgroup inputs we add the subgroup input to the end of group inputs
          subgroupIndex = groupInputs!.length;

        groupInputs!.splice(subgroupIndex, groupInputs![subgroupIndex]?.name ? 0 : 1, subgroupInput);

        if (removedInputIndexes.length > 1)
          removedInputIndexes.shift();
        else
          removedInputIndexes[0] = ++subgroupIndex;
      });

      groupInputs = groupInputs!.filter((i: { name: any; }) => i.name);
      groupInputs.filter((grInput: WidgetInput) => this.isSubgroupElement(grInput)).forEach((subGrInput: { subgroupWidth: { [x: string]: any; }; width: any; }) => {
        if (!subGrInput.subgroupWidth) return;

        subGrInput.width = subGrInput.subgroupWidth[subgroupKey.toLowerCase()] || subGrInput.width;
      });
    } catch (error) {
      if (error instanceof Error) {
        console.error("Error with subgroups :>>", error.toString());
      } else {
        console.error("Error with subgroups :>>", String(error));
      }
    }
  }

  removeFilter() {
    if (this.onUpdate)
      this.onUpdate.emit({ field: this.field, form: this.form, remove: true })
  }

  setType(type: any) {
    if (this.form.type != type && this.onUpdate) {
      this.form.type = type;
      this.onUpdate.emit({ field: this.field, form: this.form, typeChanged: true })
    }
  }

  handleAction(action: string) {
    this.onActions.emit({ name: action })
  }

  getTypeIcon(type: string): string {
    return this.types[type] || 'arrow_right';
  }

  onDatePickerChange(newDate: string, config: WidgetInput): void {
    config.value = newDate;
    this._validDateCache.set(config, newDate);

    if (config.update) {
      config.update({ target: { value: newDate } }, null, 'date');
    }
  }

  getValidDate(value: any): string {
    if (this._validDateCache.has(value)) {
      return this._validDateCache.get(value)!;
    }

    let validDate: string;

    if (!value || value === '') {
      validDate = new Date().toISOString();
    } else {
      const testDate = new Date(value);

      if (isNaN(testDate.getTime())) {
        validDate = new Date().toISOString();
      } else {
        validDate = value;
      }
    }

    this._validDateCache.set(value, validDate);
    return validDate;
  }
}
